import { Location } from 'history';
import { get } from 'lodash';
import qs from 'query-string';

import { FIELD_PATHS, FIELD_VALUES, URL_PARAM_APPLICANT_ID, URL_PARAM_APPLICATION_ID } from '@funnels/constants';
import { FunnelConfig } from '@funnels/customer-view/src/configs';
import { PAGE_TITLE } from '@funnels/customer-view/src/configs/FunnelTwo/constants';
import { StoreApplicant } from '@funnels/types';
import { FunnelApplicants } from '@funnels/ui-layouts/customer-view';
import {
  AdditionalEmployments,
  CurrentEmployment,
  EmploymentHistory,
  EmploymentSummary,
} from '@funnels/ui-layouts/customer-view/Employment';
import { getBrandSpecificBaseURL, getDashboardUrl, getEmploymentTypeAndIndex } from '@funnels/utils';

export const baseName = `/application/:${URL_PARAM_APPLICATION_ID}/applicant/:${URL_PARAM_APPLICANT_ID}/employment`;
export const additionalIncomeUrl = `${getBrandSpecificBaseURL()}${baseName}/additional-income/`;
export const incomesSummaryUrl = `${getBrandSpecificBaseURL()}/application/:${URL_PARAM_APPLICATION_ID}/income/summary/`;

export function getNextEmploymentUrl(location: Location | undefined, defaultUrl: string) {
  if (location) {
    const query = qs.parse(location.search);

    if (query.from === 'summary') {
      return { url: incomesSummaryUrl, hardRedirect: true };
    }
  }

  return { url: defaultUrl };
}

const employmentConfig: FunnelConfig = {
  baseName,
  entryPoint: () => `${baseName}/current-employment/`,
  questions: [
    // Employment
    {
      url: `${baseName}/current-employment/`,
      component: CurrentEmployment,
      target: (values: any, { location, applicantIndex = 0 }) => {
        const { employmentType } = getEmploymentTypeAndIndex(applicantIndex, values);

        if (employmentType === FIELD_VALUES.NOT_WORKING) {
          return { ...getNextEmploymentUrl(location, additionalIncomeUrl), hardRedirect: true };
        }

        return { ...getNextEmploymentUrl(location, `${baseName}/additional-employment/`) };
      },
      pageTitle: PAGE_TITLE.currentEmployment,
    },
    {
      url: `${baseName}/additional-employment/`,
      component: AdditionalEmployments,
      target: (values: any, { location }) => {
        return { ...getNextEmploymentUrl(location, additionalIncomeUrl), hardRedirect: true };
      },
      pageTitle: PAGE_TITLE.additionalEmployment,
    },

    // Income
    {
      url: `${baseName}/additional-income/`,
      component: FunnelApplicants.AdditionalIncomes,
      target: (values: any, { location, applicantIndex = 0 }) => {
        const applicants = get(values, FIELD_PATHS.APPLICANTS(), []);

        // soft redirect to income summary page
        if (applicants.length === applicantIndex + 1) {
          return {
            ...getNextEmploymentUrl(location, `/application/:${URL_PARAM_APPLICATION_ID}/income/summary/`),
          };
        }

        return {
          ...getNextEmploymentUrl(location, `${baseName}/additional-applicants-income/`),
        };
      },
      pageTitle: PAGE_TITLE.additionalIncome,
    },

    {
      url: `${baseName}/additional-applicants-income/`,
      component: FunnelApplicants.NextApplicantsEmploymentIncome,
      target: (values: any, { applicantIndex = 0 }, reduxStoreData) => {
        const applicants = get(values, FIELD_PATHS.APPLICANTS(), []);

        if (applicants.length === applicantIndex + 1) {
          return {
            url: incomesSummaryUrl,
            hardRedirect: true,
          };
        }

        const nextApplicantClientId = get(values, FIELD_PATHS.APPLICANT_CLIENT_ID(applicantIndex + 1), '');
        const nextApplicant = reduxStoreData.applicants.data.find(
          (applicant: StoreApplicant) => applicant.clientId === nextApplicantClientId
        );
        const nextApplicantId = nextApplicant?.id;

        if (!nextApplicantId) {
          return {
            url: incomesSummaryUrl,
            hardRedirect: true,
          };
        }

        return { url: `${baseName.replace(`:${URL_PARAM_APPLICANT_ID}`, nextApplicantId)}/current-employment/` };
      },
      pageTitle: PAGE_TITLE.additionalApplicantsIncome,
    },

    // 3 years Employment History
    {
      url: `${baseName}/employment-history/`,
      component: EmploymentHistory,
      target: () => {
        // redirection is in component.
        return { url: '' };
      },
      pageTitle: PAGE_TITLE.employmentHistory,
    },
    {
      url: `${baseName}/employment-history/borrower-summary/`,
      component: () => <EmploymentSummary targetApplicantRole={FIELD_VALUES.BORROWER} />,
      target: (_values: any, { params }) => {
        const applicationId = params![URL_PARAM_APPLICATION_ID];
        return { url: getDashboardUrl(applicationId), hardRedirect: true };
      },
      pageTitle: PAGE_TITLE.borrowerEmploymentHistorySummary,
    },
    {
      url: `${baseName}/employment-history/guarantor-summary/`,
      component: () => <EmploymentSummary targetApplicantRole={FIELD_VALUES.GUARANTOR} />,
      target: (_values: any, { params }) => {
        const applicationId = params![URL_PARAM_APPLICATION_ID];
        return { url: getDashboardUrl(applicationId), hardRedirect: true };
      },
      pageTitle: PAGE_TITLE.guarantorEmploymentHistorySummary,
    },
  ],
};

export default employmentConfig;
